// File: packages/core/src/hooks/useSessionWithAuth.js
import { useEffect, useRef, useCallback, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSystemStatus } from '../context/SystemStatusContext';
import { useSessionState } from './session/useSessionState';
import { useSseManager } from './session/useSseManager';
import { useSessionInitializer } from './session/useSessionInitializer';
import { useSessionReconnection } from './session/useSessionReconnection';
import { useSessionActions } from './session/useSessionActions';

const CONNECTION_STATUS_DELAY_MS = 3000;

const useVisibilityChange = (onVisible) => {
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                onVisible();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [onVisible]);
};

const useSessionWithAuth = ({ onResourceUpdateNeeded, onNewSessionCreated, onResourceSync, onAppEvent, sessionInitStrategy, defaultSessionOptions = {}, sseEventFilter, activeResourceId } = {}) => {
    const state = useSessionState();
    const {
        sessionId, isConnected, isInitializing, processingStatus,
        hasAuthFailed, isReconnecting, timelineItems, sessionContext,
        showProcessUpdates, isSending, needsNewSession, reconnectAttempts, teamName,
        preChatState, activeWidgets, isIdleDisconnected,
        setProcessingStatus, setShowProcessUpdates,
        setSessionContext, setTeamName,
        setActiveWidgets, setIsIdleDisconnected
    } = state;

    const { user, isAuthenticated, markSessionAsInitialized, resetSessionInitialization, setIsSubscriptionRequired } = useAuth();
    const { addMessage, removeMessage } = useSystemStatus();
    
    const sseClient = useRef(null);
    const isMounted = useRef(true);
    const processedItemIds = useRef(new Set());
    const processingStatusRef = useRef(processingStatus);
    const initializeSessionRef = useRef(() => {});
    const reconnectTimerRef = useRef(null);
    const isReconnectingRef = useRef(isReconnecting);
    const reconnectPendingOnShutdownRef = useRef(state.reconnectPendingOnShutdown);
    const initialSessionEstablished = useRef(false);
    const sessionToReconnectRef = useRef(null);
    const onResourceUpdateNeededRef = useRef(onResourceUpdateNeeded);
    const onNewSessionCreatedRef = useRef(onNewSessionCreated);
    const onResourceSyncRef = useRef(onResourceSync);
    const onAppEventRef = useRef(onAppEvent);
    const messageIdTrackersRef = useRef({});
    const initializingTimerRef = useRef(null);
    const reconnectMessageTimerRef = useRef(null);
    const [isResolvingStrategy, setIsResolvingStrategy] = useState(false);
    
    // Track user activity to prevent infinite reconnect loops on idle
    const lastUserActivity = useRef(Date.now());

    useEffect(() => {
        let throttleTimer = null;
        const updateActivity = () => {
            if (!throttleTimer) {
                lastUserActivity.current = Date.now();
                throttleTimer = setTimeout(() => {
                    throttleTimer = null;
                }, 1000);
            }
        };
        
        window.addEventListener('mousedown', updateActivity);
        window.addEventListener('keydown', updateActivity);
        window.addEventListener('touchstart', updateActivity);
        window.addEventListener('scroll', updateActivity);
        window.addEventListener('click', updateActivity);
        
        return () => {
            window.removeEventListener('mousedown', updateActivity);
            window.removeEventListener('keydown', updateActivity);
            window.removeEventListener('touchstart', updateActivity);
            window.removeEventListener('scroll', updateActivity);
            window.removeEventListener('click', updateActivity);
            if (throttleTimer) clearTimeout(throttleTimer);
        };
    }, []);

    const handleAppBecomesVisible = useCallback(() => {
        console.log('[Visibility] App became visible. Checking connection status.');
        lastUserActivity.current = Date.now();

        if (reconnectTimerRef.current) {
            clearTimeout(reconnectTimerRef.current);
            reconnectTimerRef.current = null;
        }

        if (sessionId && !isConnected && !isReconnectingRef.current) {
            console.log('[Visibility] Detected disconnection while hidden. Triggering immediate reconnection.');
            
            // If user returns, clear the idle state so we can reconnect
            if (state.isIdleDisconnected) {
                console.log('[Visibility] Clearing idle disconnect state due to user return.');
                state.setIsIdleDisconnected(false);
                removeMessage('idle-disconnect');
            }

            sessionToReconnectRef.current = sessionId;
            state.setIsReconnecting(true);
            state.setReconnectAttempts(0);
        }

        if (reconnectMessageTimerRef.current) {
            clearTimeout(reconnectMessageTimerRef.current);
            reconnectMessageTimerRef.current = null;
        }
        removeMessage('reconnect-notice');

        const targetSession = sessionToReconnectRef.current || sessionId;
        if (targetSession && !isConnected) {
            console.log(`[Visibility] Attempting immediate reconnection to session ${targetSession}.`);
            initializeSessionRef.current(targetSession, {}, true).then((result) => {
                if (!isMounted.current) return;
                if (result) {
                    console.log(`[Visibility] Immediate reconnection succeeded for session ${result}.`);
                    removeMessage('reconnect-notice');
                    state.setIsReconnecting(false);
                    state.setReconnectAttempts(0);
                    sessionToReconnectRef.current = null;
                    state.setReconnectTrigger(c => c + 1);
                }
            }).catch((err) => {
                console.warn('[Visibility] Immediate reconnection attempt failed:', err.message);
            });

            reconnectMessageTimerRef.current = setTimeout(() => {
                if (isMounted.current && isReconnectingRef.current) {
                    const msgText = state.reconnectPendingOnShutdown
                        ? "The server is restarting. Reconnecting..."
                        : "Connection lost. Attempting to reconnect...";
                    addMessage({
                        id: 'reconnect-notice',
                        text: msgText,
                        severity: 'info',
                        isLoading: true
                    });
                }
            }, CONNECTION_STATUS_DELAY_MS);
        }
    }, [sessionId, isConnected, addMessage, removeMessage, state.setIsReconnecting, state.setReconnectAttempts, state.setReconnectTrigger, state.reconnectPendingOnShutdown, state.isIdleDisconnected, state.setIsIdleDisconnected]);

    useVisibilityChange(handleAppBecomesVisible);

    const {
        addTimelineItem: originalAddTimelineItem,
        resetAuthState,
        resetState,
        addResourceToContext,
        removeResourceFromContext,
        sendMessage,
        toggleWidget,
        openWidget,
        closeWidget,
    } = useSessionActions({
        ...state,
        messageIdTrackersRef,
        isMounted, processedItemIds, reconnectTimerRef, initializeSessionRef, initialSessionEstablished,
        addMessage, removeMessage, resetSessionInitialization, setIsSubscriptionRequired,
        defaultSessionOptions,
    });

    const sseEventFilterRef = useRef(sseEventFilter);
    useEffect(() => {
        sseEventFilterRef.current = sseEventFilter;
    }, [sseEventFilter]);

    const addTimelineItem = useCallback((item) => {
        if (sseEventFilterRef.current && !sseEventFilterRef.current(item)) {
            console.log('[SSE Filter] Discarding SSE event:', item);
            return;
        }
        originalAddTimelineItem(item);
    }, [originalAddTimelineItem]);

    const { initializeSession } = useSessionInitializer({
        ...state,
        isAuthenticated, markSessionAsInitialized, resetSessionInitialization, setIsSubscriptionRequired,
        addMessage, removeMessage,
        isMounted, processedItemIds, sseClient, isReconnectingRef,
        initialSessionEstablished, sessionToReconnectRef, onNewSessionCreatedRef,
        sessionId,
    });

    useEffect(() => {
        initializeSessionRef.current = initializeSession;
    }, [initializeSession]);

    useEffect(() => {
        onResourceUpdateNeededRef.current = onResourceUpdateNeeded;
        onNewSessionCreatedRef.current = onNewSessionCreated;
        onResourceSyncRef.current = onResourceSync;
        onAppEventRef.current = onAppEvent;
    }, [onResourceUpdateNeeded, onNewSessionCreated, onResourceSync, onAppEvent]);

    useEffect(() => {
        if (isReconnecting) {
            if (reconnectMessageTimerRef.current) clearTimeout(reconnectMessageTimerRef.current);
            if (document.visibilityState !== 'visible') {
                return;
            }
            reconnectMessageTimerRef.current = setTimeout(() => {
                if (isMounted.current && isReconnectingRef.current) {
                    const msgText = state.reconnectPendingOnShutdown
                        ? "The server is restarting. Reconnecting..."
                        : "Connection lost. Attempting to reconnect...";
                    addMessage({
                        id: 'reconnect-notice',
                        text: msgText,
                        severity: 'info',
                        isLoading: true
                    });
                }
            }, CONNECTION_STATUS_DELAY_MS);
        } else {
            if (reconnectMessageTimerRef.current) {
                clearTimeout(reconnectMessageTimerRef.current);
                reconnectMessageTimerRef.current = null;
            }
            removeMessage('reconnect-notice');
        }
        return () => {
            if (reconnectMessageTimerRef.current) clearTimeout(reconnectMessageTimerRef.current);
        };
    }, [isReconnecting, state.reconnectPendingOnShutdown, addMessage, removeMessage]);

    useEffect(() => {
        const handleReconnect = () => {
            removeMessage('auth-error');
            if (resetAuthState) resetAuthState();
            if (initializeSessionRef.current) initializeSessionRef.current(sessionId);
        };

        if (initializingTimerRef.current) {
            clearTimeout(initializingTimerRef.current);
            initializingTimerRef.current = null;
        }

        if (isInitializing) {
            initializingTimerRef.current = setTimeout(() => {
                if (isMounted.current) {
                    addMessage({ id: 'session-status', text: 'Initializing session...', severity: 'info', isLoading: true });
                }
            }, 3000);
        } else if (isReconnecting) {
             removeMessage('session-status');
        } else if (hasAuthFailed) {
            addMessage({
                id: 'session-status',
                text: 'Authentication failed. Please try reconnecting.',
                severity: 'error',
                action: { label: 'Reconnect', onClick: handleReconnect }
            });
        } else {
            removeMessage('session-status');
        }
        return () => {
            if (initializingTimerRef.current) clearTimeout(initializingTimerRef.current);
        };
    }, [isInitializing, isConnected, hasAuthFailed, isReconnecting, addMessage, removeMessage, sessionId, resetAuthState]);

    useEffect(() => { isReconnectingRef.current = isReconnecting; }, [isReconnecting]);
    useEffect(() => { reconnectPendingOnShutdownRef.current = state.reconnectPendingOnShutdown; }, [state.reconnectPendingOnShutdown]);
    useEffect(() => { processingStatusRef.current = processingStatus; }, [processingStatus]);

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
            if (sseClient.current) { sseClient.current.disconnect(); }
            if (reconnectTimerRef.current) { clearTimeout(reconnectTimerRef.current); }
            if (reconnectMessageTimerRef.current) { clearTimeout(reconnectMessageTimerRef.current); }
        };
    }, []);

    useSseManager({
        ...state,
        messageIdTrackersRef,
        user, isAuthenticated, sseClient, isMounted, processingStatusRef,
        isReconnectingRef, reconnectPendingOnShutdownRef,
        addTimelineItem,
        resetSessionInitialization, setIsSubscriptionRequired, addMessage, removeMessage,
        onResourceUpdateNeededRef, onResourceSyncRef, onAppEventRef, sessionToReconnectRef,
        lastUserActivity, // Pass the activity ref
        setIsIdleDisconnected, // Pass the setter
    });

    useSessionReconnection({
        ...state,
        isMounted, isReconnectingRef, initializeSessionRef, reconnectTimerRef, sessionToReconnectRef,
        addMessage,
        removeMessage,
    });

    useEffect(() => {
        if (needsNewSession) {
            console.log("[useSessionWithAuth] needsNewSession is true, initializing a new session.");
            state.setNeedsNewSession(false);
            initializeSessionRef.current(null, defaultSessionOptions);
        }
    }, [needsNewSession, defaultSessionOptions, state.setNeedsNewSession]);

    useEffect(() => {
        if (isAuthenticated && !sessionId && !isInitializing && !isReconnecting && !initialSessionEstablished.current) {
            const runAutoConnect = async () => {
                if (sessionInitStrategy) {
                    if (isResolvingStrategy) return;
                    setIsResolvingStrategy(true);
                    initialSessionEstablished.current = true;
                    console.log("[useSessionWithAuth] Executing session initialization strategy...");
                    try {
                        const strategyResult = await sessionInitStrategy(user);
                        if (strategyResult?.preventAutoStart) {
                            console.log("[useSessionWithAuth] Strategy requested to prevent auto-start.");
                            setIsResolvingStrategy(false);
                            return;
                        }
                        const options = {
                            ...defaultSessionOptions,
                            ...(strategyResult || {})
                        };
                        console.log("[useSessionWithAuth] Strategy resolved. Initializing session with:", options);
                        await initializeSessionRef.current(null, options);
                    } catch (e) {
                        console.error("[useSessionWithAuth] Session strategy failed:", e);
                        await initializeSessionRef.current(null, defaultSessionOptions);
                    } finally {
                        setIsResolvingStrategy(false);
                    }
                } else {
                    console.log("[useSessionWithAuth] Auto-connecting default session.");
                    initialSessionEstablished.current = true;
                    initializeSessionRef.current(null, defaultSessionOptions);
                }
            };
            runAutoConnect();
        }
    }, [
        isAuthenticated,
        sessionId,
        isInitializing,
        isReconnecting,
        defaultSessionOptions,
        sessionInitStrategy,
        isResolvingStrategy,
        user
    ]);

    return {
        sessionId,
        isConnected,
        isInitializing,
        timelineItems,
        processingStatus,
        showProcessUpdates,
        isSending,
        hasAuthFailed,
        setProcessingStatus,
        setShowProcessUpdates,
        sendMessage,
        initializeSession,
        resetState,
        resetAuthState,
        sessionContext,
        teamName,
        setTeamName,
        addResourceToContext,
        removeResourceFromContext,
        addTimelineItem,
        preChatState,
        setSessionContext,
        activeWidgets,
        toggleWidget,
        openWidget,
        closeWidget,
        setActiveWidgets,
    };
};

export default useSessionWithAuth;